/**
 * RecentsView - Call history list with call back buttons
 */

'use client';

import React, { useMemo, useState } from 'react';
import {
  Phone,
  Video,
  Search,
  History,
  PhoneMissed,
  PhoneIncoming,
  PhoneOutgoing,
  PhoneOff,
} from 'lucide-react';
import type { RecentsViewProps, CallLog, RecentsScope } from '../types';
import { cn } from '@/lib/utils';
import BrandedLoadingSpinner from '@/components/shared/BrandedLoadingSpinner';

function isLogSip(log: CallLog): boolean {
  return !!(log.is_sip_call || log.call_type === 'sip_inbound' || log.call_type === 'sip_outbound');
}

function sipNumberToDial(log: CallLog, isOutgoing: boolean): string | null {
  if (isOutgoing) {
    return (log.target_number || log.caller_number)?.trim() || null;
  }
  return (log.caller_number || log.target_number)?.trim() || null;
}

export function RecentsView({
  callLogs,
  loading,
  searchQuery,
  onSearchChange,
  onCallBack,
  onDialNumber,
  currentUserId,
  isDark,
  primaryColor,
  recentsScope: recentsScopeProp,
  onRecentsScopeChange,
  recentsTimeSort = 'newest',
  hideRecentsToolbar = false,
}: RecentsViewProps) {
  const [internalScope, setInternalScope] = useState<RecentsScope>('all');
  const scopeControlled =
    recentsScopeProp !== undefined && onRecentsScopeChange !== undefined;
  const scope = scopeControlled ? recentsScopeProp : internalScope;
  const setScope = (s: RecentsScope) => {
    if (scopeControlled) {
      onRecentsScopeChange!(s);
    } else {
      setInternalScope(s);
    }
  };

  const formatDuration = (seconds: number): string => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const searchFiltered = useMemo(
    () =>
      callLogs.filter((log) => {
        if (!searchQuery) return true;
        const name = log.caller_id === currentUserId ? log.target_user_name : log.caller_name;
        const number = log.caller_number || log.target_number || '';
        return (
          name?.toLowerCase().includes(searchQuery.toLowerCase()) || number.includes(searchQuery)
        );
      }),
    [callLogs, searchQuery, currentUserId],
  );

  const filteredLogs = useMemo(() => {
    if (scope === 'sip') return searchFiltered.filter(isLogSip);
    if (scope === 'team') return searchFiltered.filter((l) => !isLogSip(l));
    return searchFiltered;
  }, [searchFiltered, scope]);

  const orderedLogs = useMemo(() => {
    const arr = [...filteredLogs];
    const mult = recentsTimeSort === 'oldest' ? 1 : -1;
    arr.sort(
      (a, b) => mult * (new Date(a.started_at).getTime() - new Date(b.started_at).getTime()),
    );
    return arr;
  }, [filteredLogs, recentsTimeSort]);

  const emptyHint =
    scope === 'sip'
      ? 'No SIP or PSTN calls in this range.'
      : scope === 'team'
        ? 'No app (team) calls in this range.'
        : 'Your recent calls will appear here';

  return (
    <div className="min-h-0 p-3 sm:p-4">
      {!hideRecentsToolbar ? (
        <>
          <div className={`relative mb-4`}>
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search call history..."
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              className={`w-full rounded-lg border py-2.5 pl-9 pr-4 text-sm focus:border-transparent focus:ring-2 ${
                isDark
                  ? 'border-gray-600 bg-gray-700 text-white placeholder-gray-400'
                  : 'border-gray-300 bg-white text-gray-900 placeholder-gray-500'
              }`}
              style={{ '--tw-ring-color': primaryColor } as React.CSSProperties}
            />
          </div>

          <div className="mb-3 flex flex-wrap gap-1.5">
            {(['all', 'sip', 'team'] as const).map((key) => (
              <button
                key={key}
                type="button"
                onClick={() => setScope(key)}
                className={cn(
                  'rounded-full px-3 py-1 text-xs font-medium transition-colors',
                  scope === key
                    ? 'text-white'
                    : isDark
                      ? 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200',
                )}
                style={scope === key ? { backgroundColor: primaryColor } : undefined}
              >
                {key === 'all' ? 'All calls' : key === 'sip' ? 'SIP / PSTN' : 'Team (app)'}
              </button>
            ))}
          </div>
        </>
      ) : null}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <BrandedLoadingSpinner mode="compact" size="sm" showSurface={false} showProgressBar={false} />
        </div>
      ) : orderedLogs.length === 0 ? (
        <div className={`py-12 text-center ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          <History className="mx-auto mb-3 h-12 w-12 opacity-50" />
          <p>No call history</p>
          <p className="mt-1 text-sm opacity-70">{emptyHint}</p>
        </div>
      ) : (
        <div className="min-h-0 space-y-2">
          {orderedLogs.map((log) => {
            const isSipCall =
              !!log.is_sip_call || log.call_type === 'sip_inbound' || log.call_type === 'sip_outbound';
            const isOutgoing = isSipCall
              ? log.direction === 'outbound' || log.call_type === 'sip_outbound'
              : log.caller_id === currentUserId;

            const otherPersonName = isSipCall
              ? isOutgoing
                ? log.target_number || log.target_user_name
                : log.caller_name || log.caller_number
              : isOutgoing
                ? log.target_user_name
                : log.caller_name;

            const isMissed = log.status === 'missed';
            const isRejected = log.status === 'rejected';
            const callTime = new Date(log.started_at);
            const duration = log.duration_seconds || 0;

            const sipDial = isSipCall ? sipNumberToDial(log, isOutgoing) : null;
            const canAppCallBack = !isSipCall && !!log.target_user_id;
            const canSipRedial = isSipCall && !!sipDial;

            return (
              <div
                key={log._id}
                className={`flex items-center justify-between rounded-lg p-3 ${
                  isDark ? 'bg-gray-800' : 'bg-gray-50'
                }`}
              >
                <div className="flex min-w-0 flex-1 items-center gap-3">
                  <div
                    className={`flex h-9 w-9 items-center justify-center rounded-full ${
                      isMissed || isRejected
                        ? 'bg-red-100 dark:bg-red-900/30'
                        : isOutgoing
                          ? 'bg-blue-100 dark:bg-blue-900/30'
                          : 'bg-green-100 dark:bg-green-900/30'
                    }`}
                  >
                    {isMissed ? (
                      <PhoneMissed className="h-4 w-4 text-red-500" />
                    ) : isRejected ? (
                      <PhoneOff className="h-4 w-4 text-red-500" />
                    ) : isOutgoing ? (
                      <PhoneOutgoing className="h-4 w-4 text-blue-500" />
                    ) : (
                      <PhoneIncoming className="h-4 w-4 text-green-500" />
                    )}
                  </div>

                  <div className="min-w-0 flex-1">
                    <p
                      className={`truncate text-sm font-medium ${
                        isMissed || isRejected
                          ? 'text-red-500'
                          : isDark
                            ? 'text-white'
                            : 'text-gray-900'
                      }`}
                    >
                      {otherPersonName || 'Unknown'}
                    </p>
                    <div className={`flex flex-wrap items-center gap-1.5 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      {isSipCall ? (
                        <span className="rounded bg-purple-100 px-1 py-0.5 text-[10px] font-medium text-purple-600 dark:bg-purple-900/30 dark:text-purple-400">
                          SIP
                        </span>
                      ) : log.call_type === 'video' ? (
                        <Video className="h-3 w-3" />
                      ) : (
                        <Phone className="h-3 w-3" />
                      )}
                      <span>{callTime.toLocaleDateString([], { month: 'short', day: 'numeric' })}</span>
                      <span>•</span>
                      <span>{callTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                      {duration > 0 ? (
                        <>
                          <span>•</span>
                          <span>{formatDuration(duration)}</span>
                        </>
                      ) : null}
                    </div>
                  </div>
                </div>

                {canAppCallBack || canSipRedial ? (
                  <button
                    type="button"
                    onClick={() => {
                      if (canSipRedial && sipDial) {
                        onDialNumber(sipDial);
                      } else if (log.target_user_id) {
                        onCallBack(log.target_user_id, log.call_type === 'video' ? 'video' : 'voice');
                      }
                    }}
                    className="ml-2 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-green-500 transition-all hover:bg-green-600 active:scale-95"
                  >
                    <Phone className="h-4 w-4 text-white" />
                  </button>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default RecentsView;
